// ==================== SECURITY UTILITIES ====================
// Savidna CRM - Security layer for authentication and data protection

// ==================== PASSWORD HASHING (SHA-256 + Salt) ====================

const SALT_PREFIX = 'savidna_v1_';

// Pure JavaScript SHA-256 implementation (works in ALL environments)
// Fallback for when crypto.subtle is not available (sandboxed iframes, etc.)
function sha256Pure(message: string): string {
  function rightRotate(value: number, amount: number): number {
    return value >>> amount | value << 32 - amount;
  }
  const mathPow = Math.pow;
  const maxWord = mathPow(2, 32);

  // Initial hash values (first 32 bits of fractional parts of square roots of first 8 primes)
  let h0 = 0x6a09e667,
    h1 = 0xbb67ae85,
    h2 = 0x3c6ef372,
    h3 = 0xa54ff53a;
  let h4 = 0x510e527f,
    h5 = 0x9b05688c,
    h6 = 0x1f83d9ab,
    h7 = 0x5be0cd19;

  // Round constants
  const k = [0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2];

  // Pre-processing: convert string to UTF-8 bytes
  const bytes: number[] = [];
  for (let i = 0; i < message.length; i++) {
    const code = message.charCodeAt(i);
    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | code >> 6, 0x80 | code & 0x3f);
    } else if (code < 0xd800 || code >= 0xe000) {
      bytes.push(0xe0 | code >> 12, 0x80 | code >> 6 & 0x3f, 0x80 | code & 0x3f);
    } else {
      i++;
      const cp = 0x10000 + ((code & 0x3ff) << 10 | message.charCodeAt(i) & 0x3ff);
      bytes.push(0xf0 | cp >> 18, 0x80 | cp >> 12 & 0x3f, 0x80 | cp >> 6 & 0x3f, 0x80 | cp & 0x3f);
    }
  }

  // Padding
  const bitLength = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) {
    bytes.push(0);
  }
  // Append bit length as 64-bit big-endian
  for (let i = 56; i >= 0; i -= 8) {
    bytes.push(bitLength / mathPow(2, i) & 0xff);
  }

  // Process each 512-bit (64-byte) chunk
  for (let offset = 0; offset < bytes.length; offset += 64) {
    const w: number[] = new Array(64);

    // Copy chunk into first 16 words
    for (let i = 0; i < 16; i++) {
      const j = offset + i * 4;
      w[i] = bytes[j] << 24 | bytes[j + 1] << 16 | bytes[j + 2] << 8 | bytes[j + 3];
    }

    // Extend to 64 words
    for (let i = 16; i < 64; i++) {
      const s0 = rightRotate(w[i - 15], 7) ^ rightRotate(w[i - 15], 18) ^ w[i - 15] >>> 3;
      const s1 = rightRotate(w[i - 2], 17) ^ rightRotate(w[i - 2], 19) ^ w[i - 2] >>> 10;
      w[i] = w[i - 16] + s0 + w[i - 7] + s1 | 0;
    }

    // Compression
    let a = h0,
      b = h1,
      c = h2,
      d = h3,
      e = h4,
      f = h5,
      g = h6,
      h = h7;
    for (let i = 0; i < 64; i++) {
      const S1 = rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25);
      const ch = e & f ^ ~e & g;
      const temp1 = h + S1 + ch + k[i] + w[i] | 0;
      const S0 = rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22);
      const maj = a & b ^ a & c ^ b & c;
      const temp2 = S0 + maj | 0;
      h = g;
      g = f;
      f = e;
      e = d + temp1 | 0;
      d = c;
      c = b;
      b = a;
      a = temp1 + temp2 | 0;
    }
    h0 = h0 + a | 0;
    h1 = h1 + b | 0;
    h2 = h2 + c | 0;
    h3 = h3 + d | 0;
    h4 = h4 + e | 0;
    h5 = h5 + f | 0;
    h6 = h6 + g | 0;
    h7 = h7 + h | 0;
  }

  // Produce the final hash value (big-endian)
  const hex = (n: number) => ('00000000' + (n >>> 0).toString(16)).slice(-8);
  return hex(h0) + hex(h1) + hex(h2) + hex(h3) + hex(h4) + hex(h5) + hex(h6) + hex(h7);
}

// SHA-256 with automatic fallback: try native crypto.subtle first, fall back to pure JS
async function sha256(message: string): Promise<string> {
  // Try native crypto.subtle first (faster)
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle && typeof crypto.subtle.digest === 'function') {
      const msgBuffer = new TextEncoder().encode(message);
      const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {

    // crypto.subtle not available or failed, use fallback
  } // Pure JS fallback (works everywhere)
  return sha256Pure(message);
}
export async function hashPassword(password: string): Promise<string> {
  // Double hash with salt for extra security
  const salted = SALT_PREFIX + password;
  const firstHash = await sha256(salted);
  const secondHash = await sha256(firstHash + SALT_PREFIX);
  return secondHash;
}
export async function verifyPassword(password: string, storedHash: string): Promise<boolean> {
  const hash = await hashPassword(password);
  return hash === storedHash;
}

// Check if a stored password is already hashed (64 hex chars = SHA-256)
export function isPasswordHashed(password: string): boolean {
  if (!password || typeof password !== 'string') return false;
  return /^[a-f0-9]{64}$/.test(password);
}

// ==================== RATE LIMITING ====================

interface RateLimitEntry {
  attempts: number;
  firstAttempt: number;
  lockedUntil: number | null;
}
const RATE_LIMIT_KEY = 'savidna-rate-limit';
const MAX_ATTEMPTS = 5;
const LOCK_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const WINDOW_MS = 15 * 60 * 1000; // 15 minute window

function getRateLimitEntry(): RateLimitEntry {
  try {
    const saved = localStorage.getItem(RATE_LIMIT_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return {
    attempts: 0,
    firstAttempt: 0,
    lockedUntil: null
  };
}
function saveRateLimitEntry(entry: RateLimitEntry) {
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(entry));
}
export function checkRateLimit(): {
  allowed: boolean;
  remainingAttempts: number;
  lockedUntilMs: number | null;
  message?: string;
} {
  const entry = getRateLimitEntry();
  const now = Date.now();

  // Check if currently locked
  if (entry.lockedUntil && now < entry.lockedUntil) {
    const remainingSeconds = Math.ceil((entry.lockedUntil - now) / 1000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return {
      allowed: false,
      remainingAttempts: 0,
      lockedUntilMs: entry.lockedUntil,
      message: `Demasiados intentos fallidos. Intenta de nuevo en ${minutes}:${seconds.toString().padStart(2, '0')}`
    };
  }

  // Reset if window expired
  if (entry.firstAttempt && now - entry.firstAttempt > WINDOW_MS) {
    saveRateLimitEntry({
      attempts: 0,
      firstAttempt: 0,
      lockedUntil: null
    });
    return {
      allowed: true,
      remainingAttempts: MAX_ATTEMPTS,
      lockedUntilMs: null
    };
  }
  const remaining = MAX_ATTEMPTS - entry.attempts;
  return {
    allowed: remaining > 0,
    remainingAttempts: Math.max(0, remaining),
    lockedUntilMs: null
  };
}
export function recordFailedAttempt(): {
  locked: boolean;
  remainingAttempts: number;
  message?: string;
} {
  const entry = getRateLimitEntry();
  const now = Date.now();

  // Reset if window expired
  if (entry.firstAttempt && now - entry.firstAttempt > WINDOW_MS) {
    entry.attempts = 0;
    entry.firstAttempt = 0;
    entry.lockedUntil = null;
  }
  entry.attempts += 1;
  if (!entry.firstAttempt) entry.firstAttempt = now;
  if (entry.attempts >= MAX_ATTEMPTS) {
    entry.lockedUntil = now + LOCK_DURATION_MS;
    saveRateLimitEntry(entry);
    return {
      locked: true,
      remainingAttempts: 0,
      message: 'Cuenta bloqueada temporalmente por demasiados intentos fallidos. Espera 5 minutos.'
    };
  }
  saveRateLimitEntry(entry);
  const remaining = MAX_ATTEMPTS - entry.attempts;
  return {
    locked: false,
    remainingAttempts: remaining,
    message: `Credenciales incorrectas. ${remaining} intento(s) restante(s).`
  };
}
export function resetRateLimit() {
  localStorage.removeItem(RATE_LIMIT_KEY);
}

// ==================== SESSION MANAGEMENT ====================

interface SessionData {
  userId: string;
  createdAt: number;
  expiresAt: number;
  token: string;
}
const SESSION_KEY = 'savidna-session';
const SESSION_DURATION_MS = 50 * 60 * 1000; // 50 minutes of inactivity

function generateToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}
export function createSession(userId: string): SessionData {
  const now = Date.now();
  const session: SessionData = {
    userId,
    createdAt: now,
    expiresAt: now + SESSION_DURATION_MS,
    token: generateToken()
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}
export function extendSession(): void {
  try {
    const saved = localStorage.getItem(SESSION_KEY);
    if (!saved) return;
    const session: SessionData = JSON.parse(saved);
    const now = Date.now();
    // Only extend if session is still valid
    if (now <= session.expiresAt) {
      session.expiresAt = now + SESSION_DURATION_MS;
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    }
  } catch {}
}
export function getValidSession(): SessionData | null {
  try {
    const saved = localStorage.getItem(SESSION_KEY);
    if (!saved) return null;
    const session: SessionData = JSON.parse(saved);
    const now = Date.now();

    // Check expiration
    if (now > session.expiresAt) {
      destroySession();
      return null;
    }
    return session;
  } catch {
    destroySession();
    return null;
  }
}
export function destroySession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem('savidna-user-id'); // Clean legacy key too
}

// ==================== INPUT SANITIZATION ====================

export function sanitizeInput(input: string): string {
  return input.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;').replace(/\//g, '&#x2F;').trim();
}
export function sanitizeForDisplay(input: string): string {
  // For displaying user-generated content safely
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
}

// ==================== SECURITY HEADERS / LOGGING ====================

export function logSecurityEvent(event: 'login_success' | 'login_failed' | 'logout' | 'session_expired' | 'rate_limited' | 'data_export' | 'password_changed', _details // details param kept for API compatibility but NEVER logged (PII risk)
?: string) {
  // SECURITY: Never log details (may contain email/PII) to the browser console.
  // In production this function is completely silent.
  // For audit trails, send events to a server-side endpoint instead.
  if (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV === true) {
    // Only log the event type in development, never the details
    console.log(`[SECURITY] ${event}`);
  }
  // TODO: replace with server-side audit log call, e.g.:
  // fetch('/api/audit', { method: 'POST', body: JSON.stringify({ event, ts: Date.now() }) });
}